import { useState, useEffect, useCallback, Fragment } from 'react';
import { toast } from 'react-hot-toast';
import {
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { getCountryAdmins, toggleCountryAdminActive } from '@/services/users';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { formatDate } from '@/utils';

interface CountryAdmin {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  country: string;
  country_name: string;
  country_code: string;
  is_active: boolean;
  created_at: string;
  broker_count: number;
  company_count: number;
}

export default function CountryAdminListPage() {
  const [admins, setAdmins] = useState<CountryAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  // Global rule 1 -- search-as-you-type without a request per keystroke:
  // `search` drives the Input synchronously, `debouncedSearch` (300ms) is
  // what actually feeds the fetch below.
  const { term: search, debouncedTerm: debouncedSearch, setTerm: setSearch } = useDebouncedSearch();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fetchAdmins = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getCountryAdmins({ search: debouncedSearch });
      setAdmins(data.results || data);
    } catch {
      toast.error('Failed to load country admins');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const handleToggleActive = async (admin: CountryAdmin) => {
    try {
      const result = await toggleCountryAdminActive(admin.id);
      toast.success(result.message);
      fetchAdmins();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleExpand = (adminId: string) => {
    setExpandedId(expandedId === adminId ? null : adminId);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Country Admins</h1>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search by name, email, or country..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
        />
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="w-8 px-4 py-3"></th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Country</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Brokers</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Companies</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Enrolled</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {loading ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>
            ) : admins.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">No country admins found</td></tr>
            ) : (
              admins.map((admin) => (
                <Fragment key={admin.id}>
                  <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3">
                      <button onClick={() => handleExpand(admin.id)} className="text-gray-400 hover:text-gray-600">
                        {expandedId === admin.id ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{admin.first_name} {admin.last_name}</div>
                      <div className="text-xs text-gray-500">{admin.email}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{admin.country_name}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white">{admin.broker_count}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white">{admin.company_count}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        admin.is_active
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                      }`}>
                        {admin.is_active ? 'Active' : 'Blocked'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{formatDate(admin.created_at)}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleToggleActive(admin)}
                        className={`text-xs font-medium px-3 py-1 rounded ${
                          admin.is_active
                            ? 'bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-400'
                            : 'bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-400'
                        }`}
                      >
                        {admin.is_active ? 'Block' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                  {/* Drill-down row: read-only record details only.
                      FIX 1B: brokers + insurance-company drill-downs removed — a
                      super admin can view the record and Activate/Deactivate only. */}
                  {expandedId === admin.id && (
                    <tr>
                      <td colSpan={8} className="px-4 py-4 bg-gray-50 dark:bg-gray-900">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Email</p>
                            <p className="font-medium text-gray-900 dark:text-white">{admin.email}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Phone</p>
                            <p className="font-medium text-gray-900 dark:text-white">{admin.phone || '—'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Country</p>
                            <p className="font-medium text-gray-900 dark:text-white">{admin.country_name}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Enrolled</p>
                            <p className="font-medium text-gray-900 dark:text-white">{formatDate(admin.created_at)}</p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
